import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

const DraftProductsContext = createContext();

export const DraftProductsProvider = ({ children }) => {
  const [draftProducts, setDraftProducts] = useState([]);

  const normalizeId = id =>
    typeof id === "string" && id.includes("/") ? id.split("/").pop() : id;

  const setSessionStorageDraftProducts = useCallback(products => {
    const normalized = Array.isArray(products)
      ? products.map(p => ({ ...p, id: normalizeId(p.id) }))
      : [];
    setDraftProducts(normalized);
    try {
      sessionStorage.setItem("draftProducts", JSON.stringify(normalized));
    } catch (_) {}
  }, []); // Memoized setter

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem("draftProducts");
      if (stored) {
        const parsed = JSON.parse(stored);
        const normalized = Array.isArray(parsed)
          ? parsed.map(p => ({ ...p, id: normalizeId(p.id) }))
          : [];
        setDraftProducts(normalized);
      }
    } catch (_) {}
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  // This prevents the infinite loop where context updates -> App re-renders -> useEffect runs -> new timer -> context updates
  const contextValue = useMemo(
    () => ({
      draftProducts,
      setSessionStorageDraftProducts,
    }),
    [draftProducts, setSessionStorageDraftProducts]
  );

  return (
    <DraftProductsContext.Provider value={contextValue}>
      {children}
    </DraftProductsContext.Provider>
  );
};

export const useDraftProductsContext = () => {
  const context = useContext(DraftProductsContext);
  if (!context) {
    throw new Error(
      "useDraftProductsContext must be used within a DraftProductsProvider"
    );
  }
  return context;
};
